import * as React from "react";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import * as DTO from "../../interfaces";
import { CampaignServices, PlayersServices } from "@/services";
import { useSocialNetworks, userFullName, useSocialCoachSession, exportDataToCsv } from "@/utils";
import { SocialNetworksSection } from "@/components/SocialNetworksSection";
import { Table, Button, Spinner, Dialog, Input, Pagination, Badge } from "@/components/ui";
import { ChevronUp, ChevronDown, Clock, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import _ from "lodash";

interface OwnProps {
  campaignId?: string;
  subscribedIds: number[];
  pendingIds?: number[];
}
type Props = OwnProps;

export const EnrolledStatistics: React.FC<Props> = ({ campaignId, pendingIds = [] }) => {
  const [page, setPage] = useState<number>(0);
  const [pageSize] = useState<number>(100); // Expanded page size to 100 as requested
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(null);
  const [openPendingDialog, setOpenPendingDialog] = useState<boolean>(false);
  const [pendingSearch, setPendingSearch] = useState<string>("");
  const [syncingPlayerIds, setSyncingPlayerIds] = useState<number[]>([]);
  const [syncedPlayerIds, setSyncedPlayerIds] = useState<number[]>([]);

  const { coachId } = useSocialCoachSession();
  const { postableSocialNetworks } = useSocialNetworks();

  const handleSyncPlayer = async (playerId: number) => {
    if (!campaignId) return;
    setSyncingPlayerIds(prev => [...prev, playerId]);
    try {
      await CampaignServices.subscribePlayerInCampaigns(playerId, [campaignId]);
      toast.success("Player successfully subscribed to campaign");
      setSyncedPlayerIds(prev => [...prev, playerId]);
      await Promise.all([refetchStats(), refetchPending()]);
    } catch (error) {
      console.error("Failed to subscribe player in campaign", error);
      toast.error("Failed to subscribe player to campaign");
    } finally {
      setSyncingPlayerIds(prev => prev.filter(id => id !== playerId));
    }
  };

  // Export pending players list to CSV using established helper
  const handleExportPending = () => {
    const formattedData = pendingPlayers.map(player => ({
      Name: userFullName(player.playerAccount),
      Email: player.playerAccount.email || "",
      Status: "Pending Social Connection",
    }));
    exportDataToCsv("Pending_Campaign_Enrollments", formattedData);
  };

  // TanStack React Query to fetch campaign statistics
  const {
    data: statsData,
    isLoading,
    refetch: refetchStats,
  } = useQuery({
    queryKey: ["campaignStatistics", campaignId, page, pageSize],
    queryFn: async () => {
      return await CampaignServices.statistics(campaignId!, "info.firstName,asc", page, pageSize);
    },
    enabled: !!campaignId,
  });

  // Fetch pending players full accounts details
  const {
    data: pendingPlayers = [],
    isLoading: isLoadingPending,
    refetch: refetchPending,
  } = useQuery({
    queryKey: ["pendingPlayers", coachId, pendingIds],
    queryFn: async () => {
      if (!pendingIds || pendingIds.length === 0) return [];
      const response = await PlayersServices.searchSubscribedAllPlayersByTag(coachId!, {
        playerIds: pendingIds,
      });
      return response.content || [];
    },
    enabled: !!coachId && !!pendingIds && pendingIds.length > 0,
  });

  const playersList = statsData?.content || [];

  // Map campaign subscribed statistics to standardised campaign stats structure
  const playersWithStats = useMemo<DTO.PlayerWithCampaignStats[]>(() => {
    return playersList.map(player => {
      return {
        info: player.playerAccount,
        photoUrl: player.playerAccount.photoUrl,
        email: player.playerAccount.email,
        countPromptReceived: player.countPromptReceived,
        countPromptPosted: player.countPromptPosted,
        countPromptRemaining: player.countPromptRemaining,
      } as DTO.PlayerWithCampaignStats;
    });
  }, [playersList]);

  // Handle client-side sorting toggles
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  // Perform sorting on loaded page items for instantaneous, fluid UX
  const sortedPlayers = useMemo(() => {
    if (!sortColumn || !sortDirection) return playersWithStats;
    return _.orderBy(playersWithStats, [sortColumn], [sortDirection]);
  }, [playersWithStats, sortColumn, sortDirection]);

  // Filter pending players list locally in-memory
  const filteredPendingPlayers = useMemo(() => {
    const list = pendingPlayers || [];
    const remaining = list.filter(player => !syncedPlayerIds.includes(player.playerAccount.playerId!));
    if (!pendingSearch.trim()) return remaining;
    const query = pendingSearch.toLowerCase();
    return remaining.filter(player => {
      const name = userFullName(player.playerAccount).toLowerCase();
      const email = (player.playerAccount.email || "").toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [pendingPlayers, pendingSearch, syncedPlayerIds]);

  return (
    <div className="w-full relative min-h-[300px]">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-50">
          <Spinner className="size-8" />
        </div>
      )}

      <div className="space-y-4">
        {/* Pending Enrollments Indicator Banner */}
        {pendingIds && pendingIds.length > 0 && (
          <div
            onClick={() => setOpenPendingDialog(true)}
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 rounded-xl border border-orange-200 bg-orange-50/50 hover:bg-orange-50 cursor-pointer select-none transition-all duration-150 shadow-xs gap-3 group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100 text-orange-600 transition-colors group-hover:bg-orange-200 shrink-0">
                <Clock className="size-5" />
              </div>
              <div className="flex flex-col gap-0.5 text-left">
                <span className="text-sm font-semibold text-orange-950 leading-tight">Pending Enrollments</span>
                <span className="text-xs text-orange-700 leading-tight">
                  There are {pendingIds.length} {pendingIds.length === 1 ? "player" : "players"} with pending
                  enrollments waiting to connect their social media channels.
                </span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={e => {
                e.stopPropagation();
                setOpenPendingDialog(true);
              }}
              className="bg-white border-orange-200 text-orange-800 hover:bg-orange-100 hover:border-orange-300 hover:text-orange-900 font-bold gap-1 text-xs px-3.5 h-8 rounded-lg shrink-0 w-full sm:w-auto"
            >
              View Pending Players
            </Button>
          </div>
        )}

        <div className="border border-border rounded-lg overflow-hidden bg-background">
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.Head rowSpan={2} className="text-left font-semibold align-middle border-r border-border/40">
                  Name
                </Table.Head>
                <Table.Head rowSpan={2} className="text-left font-semibold align-middle border-r border-border/40">
                  Email
                </Table.Head>
                <Table.Head rowSpan={2} className="text-left font-semibold align-middle border-r border-border/40">
                  Connected Channels
                </Table.Head>
                <Table.Head
                  colSpan={3}
                  className="text-center font-bold align-middle bg-accent/30 py-1 select-none border-b border-border/60"
                >
                  Prompt Counts
                </Table.Head>
              </Table.Row>
              <Table.Row>
                <Table.Head
                  className="text-center font-semibold cursor-pointer select-none hover:bg-muted/50 transition-colors py-2 border-r border-border/40"
                  onClick={() => handleSort("countPromptReceived")}
                >
                  <div className="flex items-center justify-center gap-1 leading-tight whitespace-normal">
                    <span>Received</span>
                    {sortColumn === "countPromptReceived" &&
                      (sortDirection === "asc" ? (
                        <ChevronUp className="size-3.5 shrink-0" />
                      ) : (
                        <ChevronDown className="size-3.5 shrink-0" />
                      ))}
                  </div>
                </Table.Head>
                <Table.Head
                  className="text-center font-semibold cursor-pointer select-none hover:bg-muted/50 transition-colors py-2 border-r border-border/40"
                  onClick={() => handleSort("countPromptPosted")}
                >
                  <div className="flex items-center justify-center gap-1 leading-tight whitespace-normal">
                    <span>Completed</span>
                    {sortColumn === "countPromptPosted" &&
                      (sortDirection === "asc" ? (
                        <ChevronUp className="size-3.5 shrink-0" />
                      ) : (
                        <ChevronDown className="size-3.5 shrink-0" />
                      ))}
                  </div>
                </Table.Head>
                <Table.Head
                  className="text-center font-semibold cursor-pointer select-none hover:bg-muted/50 transition-colors py-2"
                  onClick={() => handleSort("countPromptRemaining")}
                >
                  <div className="flex items-center justify-center gap-1 leading-tight whitespace-normal">
                    <span>Remaining</span>
                    {sortColumn === "countPromptRemaining" &&
                      (sortDirection === "asc" ? (
                        <ChevronUp className="size-3.5 shrink-0" />
                      ) : (
                        <ChevronDown className="size-3.5 shrink-0" />
                      ))}
                  </div>
                </Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {sortedPlayers.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={6} className="text-center py-8 text-muted-foreground">
                    {isLoading ? "Loading statistics..." : "No players enrolled in this campaign."}
                  </Table.Cell>
                </Table.Row>
              ) : (
                sortedPlayers.map(player => {
                  const { info, countPromptReceived, countPromptPosted, countPromptRemaining } = player;
                  const playerSnIds = info.socialNetworks?.map(sn => sn.networkId);
                  return (
                    <Table.Row key={`enrolled-stats-${info.playerId}`} className="hover:bg-muted/30">
                      <Table.Cell className="align-middle text-sm font-bold text-foreground text-left border-r border-border/40">
                        {userFullName(info) || info.email}
                      </Table.Cell>
                      <Table.Cell className="align-middle text-xs text-muted-foreground text-left border-r border-border/40">
                        {info.email || "-"}
                      </Table.Cell>
                      <Table.Cell className="align-middle text-left border-r border-border/40">
                        <div className="[&_img]:p-0.5 select-none shrink-0 flex items-center gap-0">
                          <SocialNetworksSection
                            actives={playerSnIds}
                            allSocialNetworks={postableSocialNetworks}
                            size="micro"
                          />
                        </div>
                      </Table.Cell>
                      <Table.Cell className="align-middle text-center text-sm font-medium border-r border-border/40">
                        {countPromptReceived ?? 0}
                      </Table.Cell>
                      <Table.Cell className="align-middle text-center text-sm font-medium border-r border-border/40">
                        {countPromptPosted ?? 0}
                      </Table.Cell>
                      <Table.Cell className="align-middle text-center text-sm font-medium">
                        {countPromptRemaining ?? 0}
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              )}
            </Table.Body>
          </Table>
        </div>

        {/* Dynamic Pagination Footer */}
        {statsData?.totalPages && statsData.totalPages > 1 ? (
          <div className="flex items-center justify-between mt-4 bg-background border border-border rounded-lg p-3 shadow-xs">
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              Showing Page {page + 1} of {statsData.totalPages} ({statsData.totalElements} total players)
            </span>
            <Pagination
              page={page + 1}
              totalPages={statsData.totalPages}
              onPageChange={p => setPage(p - 1)}
              disabled={isLoading}
              className="justify-end w-auto mx-0"
            />
          </div>
        ) : undefined}
      </div>

      {/* Pending Players Dialog */}
      <Dialog open={openPendingDialog} onOpenChange={setOpenPendingDialog} size="xl">
        <Dialog.Header>
          <Dialog.Title>Pending Campaign Enrollments</Dialog.Title>
        </Dialog.Header>
        <Dialog.Content className="p-6">
          <p className="text-sm text-muted-foreground leading-relaxed bg-orange-50/25 border border-orange-100 p-4 rounded-xl mb-4 text-left">
            These players have been enrolled in this campaign, but must connect a social media account before
            posts/notifications will be scheduled. Once connected, posts will automatically be scheduled on their
            behalf.
          </p>

          {/* Search and Export Toolbar */}
          <div className="flex items-center gap-2 mb-3 select-none">
            <Input
              placeholder="Search by name or email..."
              value={pendingSearch}
              onChange={e => setPendingSearch(e.target.value)}
              className="max-w-xs h-9 bg-background border border-border rounded-lg text-xs"
            />
            <div className="flex-1" />
            {pendingPlayers && pendingPlayers.length > 0 && (
              <Badge
                variant="secondary"
                className="bg-orange-100 text-orange-800 hover:bg-orange-100 border-none text-xs font-extrabold px-2.5 h-9 rounded-lg flex items-center shrink-0"
              >
                {pendingPlayers.length} Pending
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={handleExportPending}
              disabled={!pendingPlayers || pendingPlayers.length === 0}
              className="h-9 px-3 text-xs font-bold gap-1.5 rounded-lg border-slate-200 shrink-0"
            >
              <Download className="size-3.5" />
              Export CSV
            </Button>
          </div>

          <div className="border border-border rounded-lg overflow-hidden bg-background max-h-[300px] overflow-y-auto no-scrollbar">
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head className="font-semibold text-left">Name</Table.Head>
                  <Table.Head className="font-semibold text-left">Email</Table.Head>
                  <Table.Head className="font-semibold text-left">Connected Channels</Table.Head>
                  <Table.Head className="font-semibold text-center w-24">Action</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {isLoadingPending ? (
                  <Table.Row>
                    <Table.Cell colSpan={4} className="text-center py-8 text-muted-foreground">
                      <Spinner className="size-5 inline-block mr-2" /> Loading pending players...
                    </Table.Cell>
                  </Table.Row>
                ) : filteredPendingPlayers?.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={4} className="text-center py-8 text-muted-foreground">
                      {pendingSearch ? "No matching pending players found." : "No pending players found."}
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  filteredPendingPlayers?.map(player => {
                    const playerSnIds = player.playerAccount.socialNetworks?.map(sn => sn.networkId) || [];
                    const hasConnectedSocialAccounts = playerSnIds.length > 0;
                    const isSyncing = syncingPlayerIds.includes(player.playerAccount.playerId!);

                    return (
                      <Table.Row key={`pending-player-${player.playerAccount.playerId}`} className="hover:bg-muted/30">
                        <Table.Cell className="align-middle text-sm font-semibold text-foreground text-left">
                          {userFullName(player.playerAccount)}
                        </Table.Cell>
                        <Table.Cell className="align-middle text-sm text-muted-foreground text-left">
                          {player.playerAccount.email}
                        </Table.Cell>
                        <Table.Cell className="align-middle text-left">
                          {hasConnectedSocialAccounts ? (
                            <div className="[&_img]:p-0.5 select-none shrink-0 flex items-center gap-0">
                              <SocialNetworksSection
                                actives={playerSnIds}
                                allSocialNetworks={postableSocialNetworks}
                                size="micro"
                              />
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">None</span>
                          )}
                        </Table.Cell>
                        <Table.Cell className="align-middle text-center">
                          {hasConnectedSocialAccounts ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                              onClick={() => handleSyncPlayer(player.playerAccount.playerId!)}
                              disabled={isSyncing}
                              title="Sync subscription"
                            >
                              <RefreshCw className={`size-4 ${isSyncing ? "animate-spin text-primary" : ""}`} />
                            </Button>
                          ) : (
                            "-"
                          )}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })
                )}
              </Table.Body>
            </Table>
          </div>
        </Dialog.Content>
        <Dialog.Footer>
          <Button type="button" onClick={() => setOpenPendingDialog(false)}>
            Close
          </Button>
        </Dialog.Footer>
      </Dialog>
    </div>
  );
};
